/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type KeyboardEvent,
	type MouseEvent,
	type Ref,
	useCallback,
	useRef,
} from 'react';

import { css, jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { mergeRefs } from 'use-callback-ref';

import { ButtonItem, LinkItem } from '@atlaskit/menu';
import type { ButtonItemProps, LinkItemProps } from '@atlaskit/menu/types';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { MenuItem, WrapperRenderProps } from '../../../types';
import { useMenuControl } from '../../../utils';

import { MenuSkeletonItem } from './menu-skeleton-item';

const customItemStyles = css({
	paddingLeft: token('space.100', '8px'),
	paddingRight: token('space.100', '8px'),
	width: '230px',
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'.custom-item-style': {
		borderRadius: '4px',
		paddingLeft: token('space.100', '8px'),
		paddingRight: token('space.100', '8px'),
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-before]': {
			minWidth: 'unset',
		},
	},
});

const textStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		fontFamily: 'font.family.body',
		textOverflow: 'ellipsis',
		display: 'block',
	},
});

// Function to check if the element's text is truncated
function isTextClamped(labelElement: HTMLElement, boxElement: HTMLElement): boolean {
	return labelElement.scrollWidth > boxElement.scrollWidth;
}

const Item = forwardRef<HTMLAnchorElement | HTMLButtonElement, Omit<MenuItem, 'ref'>>(
	(
		{
			onClick,
			isLoading,
			loadCreateContent: createContent,
			onClickCloseMenu = true,
			children,
			testId,
			isDisabled,
			href,
			target,
			elemBefore,
			Wrapper,
		},
		forwardRef,
	) => {
		const menuControl = useMenuControl();
		const labelRef = useRef<HTMLElement>(null);

		const buttonClick = useCallback(
			(event: MouseEvent | KeyboardEvent) => {
				if (createContent) {
					menuControl.load(createContent);
				} else if (onClickCloseMenu) {
					menuControl.close();
				}

				onClick?.(event);
			},
			[menuControl, onClick, onClickCloseMenu, createContent],
		);

		const renderButton = useCallback(
			(props?: WrapperRenderProps) => {
				const { triggerProps } = props || {};
				const { ref: triggerRef, ...restTriggerProps } = triggerProps || {};

				const Icon = elemBefore;
				const baseProps = {
					testId,
					iconBefore:
						elemBefore && Icon ? <Icon label="" color={token('color.text.subtle')} /> : undefined,
					onClick: buttonClick,
					className: 'custom-item-style',
					children: (
						<span css={textStyle}>
							<Text ref={labelRef} maxLines={1} color="color.text.subtle" weight="medium">
								{children}
							</Text>
						</span>
					),
				};

				const canTooltipAppear: (() => boolean) | undefined = () => {
					return labelRef.current && mergedRef.current
						? isTextClamped(labelRef.current, mergedRef.current)
						: false;
				};

				const mergedRef = mergeRefs([
					...(forwardRef ? [forwardRef] : []),
					...(triggerRef ? [triggerRef] : []),
				]);

				const renderItem = (
					Item: typeof LinkItem | typeof ButtonItem,
					props: LinkItemProps | ButtonItemProps,
				) => {
					return (
						<Tooltip content={children} canAppear={canTooltipAppear}>
							<Item {...props} />
						</Tooltip>
					);
				};

				const linkProps: LinkItemProps = {
					...baseProps,
					href,
					target,
					...restTriggerProps,
					...(mergedRef && { ref: mergedRef as Ref<HTMLAnchorElement> }),
				};

				const buttonProps: ButtonItemProps = {
					...baseProps,
					isDisabled,
					...restTriggerProps,
					...(mergedRef && { ref: mergedRef as Ref<HTMLButtonElement> }),
				};

				return href ? renderItem(LinkItem, linkProps) : renderItem(ButtonItem, buttonProps);
			},
			[testId, elemBefore, buttonClick, children, forwardRef, href, isDisabled, target],
		);

		if (isLoading) {
			return <MenuSkeletonItem testId={`loading-${testId}`} />;
		}

		if (Wrapper) {
			return <Wrapper>{(props?: WrapperRenderProps) => renderButton(props)}</Wrapper>;
		}

		return renderButton();
	},
);

export default function MenuContentItem({ item }: { item: MenuItem }) {
	const { ref, ...props } = item;
	return (
		<ErrorBoundary fallback={null}>
			<div css={customItemStyles}>
				<Item {...props} ref={ref} />
			</div>
		</ErrorBoundary>
	);
}
